// ─────────────────────────────────────────────────────────
// SELECTRA TECHNOLOGIES — SITE DATA
// Sourced from the live selectra.co.in site content. Edit this
// file to update any copy, contact detail, product or location
// without touching component code.
// ─────────────────────────────────────────────────────────

import { images } from "./images";

export const brand = {
  name: "SELECTRA TECHNOLOGIES",
  tagline: "SELECTIVE PARTNER FOR SOLAR ENGINEERING SOLUTIONS",
  logo: "/assets/selogo.png",
  supporting:
    "Intelligent solutions for smart infrastructure — Renewable Energy, Power Backup and Intelligent Battery Monitoring Solutions.",
};

export const contact = {
  phone: "+91 98468 53000",
  email: "info@selectra.co.in",
};

export const locations = [
  {
    id: "location-one",
    slug: "registered-office",
    code: "01",
    name: "Selectra Technologies — Registered Office",
    city: "Thrissur",
    region: "Kerala",
    label: "Registered Office",
    address:
      "Door No: 3/470/H, Kavungal Arcade, Vellikulangara Rd, Kodakara, Thrissur – 680684",
    phone: contact.phone,
    email: contact.email,
    hours: "Mon – Sat, 9:30 AM – 6:30 PM",
    description:
      "Our registered office and operational base in Kodakara, Thrissur — where our engineering and project teams plan, design and coordinate installations across Kerala.",
    stat: { label: "Years In Operation", value: "2018–" },
    image: images.heroRooftop,
    // Seed data only — the live list on the site is managed per-browser
    // via the "Manage Projects" panel on the location page (browser
    // storage), so visitors editing there won't overwrite this file.
    projects: [
      {
        id: "reg-proj-1",
        title: "Residential Rooftop On-Grid",
        type: "Residential",
        capacity: "5 kW",
        year: "2024",
        description:
          "On-grid rooftop installation with net metering for a family home in Kodakara.",
        image: images.panelsCloseup,
      },
      {
        id: "reg-proj-2",
        title: "Intelligent Battery Monitoring — Data Centre",
        type: "Battery Monitoring",
        capacity: "Data Centre UPS",
        year: "2023",
        description:
          "Automated battery health monitoring deployed for a UPS system supporting critical infrastructure.",
        image: images.batteryRoom,
      },
      {
        id: "reg-proj-3",
        title: "Solar Street Lighting — District Roads",
        type: "Street Lighting",
        capacity: "150 units",
        year: "2023",
        description:
          "LED solar street lights with integrated battery banks installed along district roads near Thrissur.",
        image: images.streetLight,
      },
    ],
  },
  {
    id: "location-two",
    slug: "branch-office",
    code: "02",
    name: "Selectra Technologies — Branch Office",
    city: "Palakkad",
    region: "Kerala",
    label: "Branch Office",
    address: "Door No: 11/874-9, JM Complex, Robinson Road, Palakkad – 678001, Kerala",
    phone: contact.phone,
    email: contact.email,
    hours: "Mon – Sat, 9:30 AM – 6:30 PM",
    description:
      "Our Palakkad branch office extends the same design and installation standard closer to customers across the district and neighbouring areas.",
    stat: { label: "Coverage", value: "Palakkad & Nearby" },
    image: images.solarSunset,
    projects: [
      {
        id: "branch-proj-1",
        title: "Commercial Off-Grid System",
        type: "Commercial",
        capacity: "20 kW",
        year: "2024",
        description:
          "Complete off-grid solar and storage system for a commercial property in Palakkad town.",
        image: images.aerialPanels,
      },
      {
        id: "branch-proj-2",
        title: "Power Backup Installation",
        type: "Power Backup",
        capacity: "10 kVA",
        year: "2023",
        description:
          "High-capacity inverter and backup system supporting a small business through grid outages.",
        image: images.engineerInstall,
      },
    ],
  },
];

export const services = [
  {
    id: "rooftop-on-grid",
    number: "01",
    name: "Solar Rooftop On-Grid Solutions",
    short:
      "A reliable, easy-to-install system with a 25-year lifespan using your existing roof space.",
    description:
      "The on-grid rooftop system converts sunlight into grid-compatible power. Panels mounted on the roof feed an inverter that syncs with grid electricity, and net metering lets you send excess power back to the grid — cutting your electricity bill while staying connected to the public utility.",
    image: images.heroRooftop,
  },
  {
    id: "off-grid",
    number: "02",
    name: "Solar Off-Grid Solutions",
    short:
      "Complete end-to-end storage solution — power independence with no grid connection.",
    description:
      "With no connection to the national grid, our off-grid systems store solar power in a battery bank during the day and release it at night, giving you complete independence and protection from power cuts.",
    image: images.solarSunset,
  },
  {
    id: "hybrid",
    number: "03",
    name: "Hybrid Solutions",
    short: "The best of both grids — solar priority with grid backup when it's needed.",
    description:
      "Hybrid systems use solar inverters and batteries to store usable electricity for later use, combining the savings of solar with the reliability of grid backup in a single installation.",
    image: images.aerialPanels,
  },
  {
    id: "solar-panels",
    number: "04",
    name: "Solar Panels",
    short: "Photovoltaic panels that convert sunlight into usable electricity.",
    description:
      "Our panels are made of photovoltaic cells that capture solar energy and convert it into direct current (DC), forming the foundation of every system we install.",
    image: images.panelsCloseup,
  },
  {
    id: "on-grid-inverters",
    number: "05",
    name: "Residential On-Grid Inverters",
    short: "The essential link that converts DC solar power into usable AC electricity.",
    description:
      "On-grid inverters convert the DC electricity generated by your panels into AC electricity that powers appliances and devices throughout the home. We offer a comprehensive range from 1 kW to 60 kW, suited to private homes and commercial roofs alike.",
    image: images.engineerInstall,
  },
  {
    id: "street-lights",
    number: "06",
    name: "Solar Street Lights",
    short: "High-efficiency LED lighting for streets, parks and pathways.",
    description:
      "Our street lighting systems combine an internal LiFePO4 battery bank with high-efficiency monocrystalline panels in one compact unit — configurable for customised public and private lighting applications.",
    image: images.streetLight,
  },
  {
    id: "solar-dryers",
    number: "07",
    name: "Solar Dryers",
    short: "Hygienic, cost-effective drying for agricultural products and food.",
    description:
      "Solar dryers use the sun's energy to create a controlled drying environment for vegetables, fish, mushrooms and other produce — hygienic, free of UV colour degradation, and available with biogas, LPG or electric backup for year-round operation.",
    image: images.ruralFarm,
  },
  {
    id: "power-backup",
    number: "08",
    name: "Power Backup Solutions",
    short: "High-capacity inverters that power your entire home or business through outages.",
    description:
      "From design and planning to financing, installation and system monitoring, we handle the full journey — offering inverters built to power heavy-duty and sensitive appliances in residential and commercial spaces.",
    image: images.batteryRoom,
  },
  {
    id: "battery-monitoring",
    number: "09",
    name: "Intelligent Battery Monitoring Systems",
    short: "Automated monitoring that catches battery risk months before it becomes a failure.",
    description:
      "An automated battery monitoring system for operations where power availability is critical — delivering prompt notification of vulnerabilities so you can act before a battery becomes an operational risk, for data centres, UPS systems, utilities and remote sites.",
    image: images.teamMeeting,
  },
];

// Top-of-home trust signals (from the site's opening blurb row).
export const energyConcepts = [
  {
    number: "01",
    title: "GREAT SUPPORT",
    text: "A team that stays reachable and responsive well beyond the day of installation.",
  },
  {
    number: "02",
    title: "FULLY RESPONSIVE",
    text: "Systems and service designed around how you actually use power, not a fixed template.",
  },
  {
    number: "03",
    title: "DEDICATED TEAM",
    text: "In-house engineers who see every project through from design to commissioning.",
  },
];

export const whySelectra = [
  {
    number: "01",
    title: "BEST-IN-CLASS WARRANTY",
    text: "Every installation is backed by warranty terms we stand behind personally.",
  },
  {
    number: "02",
    title: "IN-HOUSE EXPERTS",
    text: "A trained, in-house team handles design and installation — not subcontracted labour.",
  },
  {
    number: "03",
    title: "LOCAL KNOWLEDGE",
    text: "In-depth understanding of local logistics, regulations and available incentives.",
  },
  {
    number: "04",
    title: "COMPLETION ON TIME",
    text: "Projects delivered to the timeline we commit to at the start.",
  },
  {
    number: "05",
    title: "ALWAYS REACHABLE",
    text: "If something goes wrong, our team is reachable and ready to resolve it.",
  },
];

export const projects = [
  {
    id: "proj-1",
    type: "RESIDENTIAL ON-GRID",
    location: "Thrissur",
    capacity: "5 kW",
    year: "2024",
    image: images.panelsCloseup,
  },
  {
    id: "proj-2",
    type: "COMMERCIAL OFF-GRID",
    location: "Palakkad",
    capacity: "20 kW",
    year: "2024",
    image: images.solarSunset,
  },
  {
    id: "proj-3",
    type: "STREET LIGHTING",
    location: "Thrissur District",
    capacity: "150 units",
    year: "2023",
    image: images.streetLight,
  },
  {
    id: "proj-4",
    type: "POWER BACKUP",
    location: "Palakkad",
    capacity: "10 kVA",
    year: "2023",
    image: images.batteryRoom,
  },
  {
    id: "proj-5",
    type: "BATTERY MONITORING",
    location: "Kodakara",
    capacity: "Data Centre",
    year: "2023",
    image: images.aerialPanels,
  },
];

export const testimonials = [
  {
    id: "t1",
    quote:
      "I had the pleasure of working with Selectra Technologies to install a solar power system for my business, and I was extremely impressed with their level of professionalism and expertise. The team took the time to understand my specific needs and design a system that would provide me with the maximum benefit.",
    name: "Business Customer",
    location: "Kerala",
  },
  {
    id: "t2",
    quote:
      "Selectra Technologies recently installed a solar power system for my home, and I am satisfied with the results. From start to finish, the team was professional, knowledgeable, and dedicated to ensuring that I got the best possible system for my needs.",
    name: "Residential Customer",
    location: "Kerala",
  },
];

export const nav = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Products", path: "/services" },
  { label: "Locations", path: "/locations" },
  { label: "Contact", path: "/contact" },
];

export const mapEmbedUrl =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3924.586923884205!2d76.30466617415087!3d10.374875566473793!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba7f9cb3fbcfa3f%3A0x20ecff6ae8e256db!2sSelectra%20Technologies%20LLP!5e0!3m2!1sen!2sin!4v1682504332521!5m2!1sen!2sin";

export const socials = [
  { label: "Instagram", href: "https://instagram.com" },
  { label: "LinkedIn", href: "https://linkedin.com" },
  { label: "Facebook", href: "https://facebook.com" },
];
